import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  AssignedItemComponentType,
  ASSIGNED_ITEM_MARKING_TASK_OPTIONS,
  IBlackoutPreviewGroup,
} from '../types';
import {
  BlackoutCombinedSegment,
  BlackoutEvalResult,
  PREVIEW_WEEKDAY_LABELS,
  blockedMinutes,
  combinedDaySegments,
  dateAdd,
  evaluateBlackoutGroups,
  formatBlockedPct,
  formatDuration,
  formatHm,
  localWallClock,
  normalizePreviewGroups,
  todayInTimezone,
  weekStartOf,
} from '../blackout-hours-evaluator';

export type BlackoutPreviewTab = 'week' | 'day';

/**
 * Overlapping schedule preview — week grid + day timeline.
 * Accepts one or more groups; blocked when any group blocks.
 */
@Component({
  selector: 'blackout-hours-schedule-preview',
  templateUrl: './blackout-hours-schedule-preview.component.html',
  styleUrls: ['./blackout-hours-schedule-preview.component.scss'],
})
export class BlackoutHoursSchedulePreviewComponent implements OnChanges {

  @Input() groups: IBlackoutPreviewGroup[] = [];
  @Input() taskType: string = AssignedItemComponentType.SCORING;
  @Input() previewAt: Date = new Date();
  @Input() isScheduleOn: boolean = true;
  /** IANA timezone for week navigation when groups disagree */
  @Input() timezone: string;
  /** When this changes (e.g. new group vs edit), the preview collapses again. */
  @Input() contextKey = '';
  /** Open the week/day timeline immediately (scorer dashboard). */
  @Input() initiallyExpanded = false;
  /** Leader-only: blocked percentages per day and for the week. Scorers never see them. */
  @Input() showBlockedStats = true;

  isExpanded = false;

  PREVIEW_WEEKDAY_LABELS = PREVIEW_WEEKDAY_LABELS;
  /** Held as a field so `*ngFor` is not handed a fresh array on every check. */
  hourGuides = [0, 3, 6, 9, 12, 15, 18, 21];
  markingTaskOptions = ASSIGNED_ITEM_MARKING_TASK_OPTIONS;
  formatHm = formatHm;
  formatDuration = formatDuration;

  tab: BlackoutPreviewTab = 'week';
  selectedDate: string = '';
  normalizedGroups: IBlackoutPreviewGroup[] = [];
  previewClock = { date: '', minutes: 0, label: '' };
  verdict: BlackoutEvalResult = { allowed: true, reason: 'no_schedule' };
  weekDays: { date: string; label: string; sub: string; segments: BlackoutCombinedSegment[]; blockedPct: number }[] = [];
  daySegments: BlackoutCombinedSegment[] = [];
  dayLayers: { top: number; height: number; color: string; label: string }[] = [];
  previewMarkerTop = 0;
  weekBlockedPct = 0;
  weekBlockedLabel = '0%';
  todayBlockedLabel = '';

  private lastRefreshKey = '';
  private lastContextKey = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contextKey && this.contextKey !== this.lastContextKey) {
      this.lastContextKey = this.contextKey;
      this.isExpanded = this.initiallyExpanded;
      this.lastRefreshKey = '';
      if (this.isExpanded) this.refresh();
    }
    if (changes.initiallyExpanded && this.initiallyExpanded && !this.isExpanded) {
      this.isExpanded = true;
      this.refresh();
    }
    if (changes.groups || changes.previewAt || changes.taskType || changes.isScheduleOn || changes.timezone) {
      if (this.isExpanded) this.refresh();
      else this.lastRefreshKey = '';
    }
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) this.refresh();
  }

  private refreshKey(): string {
    return JSON.stringify({
      groups: this.groups,
      taskType: this.taskType,
      previewAt: this.previewAt?.getTime?.(),
      isScheduleOn: this.isScheduleOn,
      timezone: this.timezone,
    });
  }

  private resolveTimezone(): string {
    if (this.timezone) return this.timezone;
    return this.normalizedGroups[0]?.timezone || 'America/Toronto';
  }

  refresh() {
    const key = this.refreshKey();
    if (key === this.lastRefreshKey) return;
    this.lastRefreshKey = key;

    this.normalizedGroups = normalizePreviewGroups(this.groups);
    const tz = this.resolveTimezone();
    this.previewClock = localWallClock(this.previewAt, tz);
    if (!this.selectedDate) this.selectedDate = this.previewClock.date;
    this.verdict = evaluateBlackoutGroups(
      this.normalizedGroups,
      this.previewAt,
      this.taskType,
      this.isScheduleOn,
    );
    this.renderWeek(tz);
    this.renderDay(tz);
  }

  setTab(tab: BlackoutPreviewTab) {
    this.tab = tab;
  }

  selectWeekDay(date: string) {
    this.selectedDate = date;
    this.renderDay(this.resolveTimezone());
  }

  onTaskTypeChange(taskType: string) {
    this.taskType = taskType;
    this.refresh();
  }

  private renderWeek(tz: string) {
    const anchor = this.previewClock.date || todayInTimezone(tz);
    const start = weekStartOf(anchor);
    this.weekDays = [];
    let totalBlocked = 0;
    for (let i = 0; i < 7; i++) {
      const date = dateAdd(start, i);
      const segments = combinedDaySegments(
        this.normalizedGroups,
        date,
        this.taskType,
        this.isScheduleOn,
      );
      const blocked = blockedMinutes(segments);
      totalBlocked += blocked;
      this.weekDays.push({
        date,
        label: PREVIEW_WEEKDAY_LABELS[i],
        sub: date.slice(5),
        segments,
        blockedPct: this.isScheduleOn ? Math.round((blocked / 1440) * 100) : 0,
      });
    }
    this.weekBlockedPct = this.isScheduleOn ? (totalBlocked / (7 * 1440)) * 100 : 0;
    this.weekBlockedLabel = this.isScheduleOn ? formatBlockedPct(totalBlocked, 7 * 1440) : '0%';
    const todaySegs = combinedDaySegments(
      this.normalizedGroups,
      this.selectedDate || anchor,
      this.taskType,
      this.isScheduleOn,
    );
    this.todayBlockedLabel = formatDuration(blockedMinutes(todaySegs));
  }

  private renderDay(tz: string) {
    const date = this.selectedDate || this.previewClock.date || todayInTimezone(tz);
    this.daySegments = combinedDaySegments(
      this.normalizedGroups,
      date,
      this.taskType,
      this.isScheduleOn,
    );
    const H = 420;
    this.dayLayers = [];
    this.daySegments.forEach((seg) => {
      if (!seg.blocked) return;
      const top = (seg.start / 1440) * H;
      const height = Math.max(4, ((seg.end - seg.start) / 1440) * H);
      const names = seg.layers.map((l) => l.groupName || l.groupKey).join(' + ');
      const color = seg.layers.length === 1
        ? (seg.layers[0].color || '#c0392b')
        : '#96271a';
      this.dayLayers.push({
        top,
        height,
        color,
        label: (names || 'Non-working') + ' · ' + formatHm(seg.start) + '–' + (seg.end === 1440 ? '24:00' : formatHm(seg.end)),
      });
    });

    const clock = localWallClock(this.previewAt, tz);
    this.previewMarkerTop = clock.date === date ? (clock.minutes / 1440) * H : -1;
  }

  segmentTopPct(seg: BlackoutCombinedSegment): number {
    return (seg.start / 1440) * 100;
  }

  segmentHeightPct(seg: BlackoutCombinedSegment): number {
    return ((seg.end - seg.start) / 1440) * 100;
  }

  segmentClass(seg: BlackoutCombinedSegment): string {
    if (!this.isScheduleOn) return 'na';
    if (!seg.blocked) return 'allow';
    return seg.layers.length > 1 ? 'block overlap' : 'block';
  }

  segmentTitle(seg: BlackoutCombinedSegment): string {
    const span = formatHm(seg.start) + '–' + (seg.end === 1440 ? '24:00' : formatHm(seg.end));
    if (!this.isScheduleOn) return 'Schedule off · ' + span;
    if (!seg.blocked) return 'Available · ' + span;
    const names = seg.layers.map((l) => l.groupName || l.groupKey).join(', ');
    return 'Non-working' + (names ? ' (' + names + ')' : '') + ' · ' + span;
  }

  trackByDate(_: number, day: { date: string }): string {
    return day.date;
  }

  trackBySegment(_: number, seg: BlackoutCombinedSegment): string {
    return seg.start + '-' + seg.end + '-' + seg.layers.length;
  }
}
